import * as React from 'react';
import { cn } from '@wayste/shadcn/lib/utils';
import { ActionFooter, type ActionFooterProps } from './action-footer';
import { Toolbar, type ToolbarProps, type ToolbarSize, ToolbarTitle } from './toolbar';

// ─── Size types ───────────────────────────────────────────────────────────────

export type CardSize = 'xs' | 'sm' | 'default' | 'lg';

// Card sizes map to Toolbar sizes for typography. Card "lg" uses the same title
// text as "default" (text-base / 16px) per Figma — only the padding differs.
const cardToToolbarSize: Record<CardSize, ToolbarSize> = {
    xs: 'xs',
    sm: 'sm',
    default: 'default',
    lg: 'default',
};

// Card "lg" needs px-6 py-5 (24px / 20px) which differs from Toolbar "default"
// (px-5 py-4). All other sizes align exactly with their Toolbar counterpart.
const cardHeaderPaddingOverride: Partial<Record<CardSize, string>> = {
    lg: 'px-6 py-5',
};

// ─── Card ─────────────────────────────────────────────────────────────────────

function Card({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card"
            className={cn(
                'bg-surface-tertiary text-text-primary border-border-default flex flex-col overflow-clip rounded-lg border',
                className,
            )}
            {...props}
        />
    );
}

// ─── CardHeader ───────────────────────────────────────────────────────────────

export interface CardHeaderProps extends Omit<ToolbarProps, 'size' | 'noBorder'> {
    /**
     * Controls padding and title text size.
     * - xs  — px-3 / py-2.5, text-sm
     * - sm  — px-4 / py-3,   text-sm
     * - default — px-5 / py-4, text-base (recommended)
     * - lg  — px-6 / py-5,   text-base (larger padding, same title size as default)
     * @default 'default'
     */
    size?: CardSize;
}

/**
 * Slot-based card header backed by Toolbar. Place a ToolbarTitle inside for
 * the title area — any additional children (buttons, badges, pills) flow to
 * the right edge automatically.
 *
 * @example
 * ```tsx
 * <CardHeader size="default">
 *   <ToolbarTitle description="Manage your settings">Account</ToolbarTitle>
 *   <Button size="sm" variant="ghost">Edit</Button>
 * </CardHeader>
 * ```
 */
function CardHeader({ size = 'default', className, ...props }: CardHeaderProps) {
    const toolbarSize = cardToToolbarSize[size];
    const paddingOverride = cardHeaderPaddingOverride[size];
    return <Toolbar data-slot="card-header" size={toolbarSize} noBorder={false} className={cn(paddingOverride, className)} {...props} />;
}

// ─── Legacy sub-components (kept for backward compat) ─────────────────────────
// Prefer ToolbarTitle inside CardHeader for new usage.

/** @deprecated Use ToolbarTitle inside CardHeader instead. */
function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="card-title" className={cn('text-text-primary text-base font-semibold leading-6', className)} {...props} />;
}

/** @deprecated Use the description prop on ToolbarTitle instead. */
function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="card-description" className={cn('text-text-secondary text-sm leading-5', className)} {...props} />;
}

/** @deprecated Place action elements directly after ToolbarTitle in CardHeader. */
function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="card-action" className={cn('flex items-center gap-3', className)} {...props} />;
}

// ─── CardContent ──────────────────────────────────────────────────────────────

const cardContentSizeVariants: Record<CardSize, string> = {
    xs: 'gap-2 p-3',
    sm: 'gap-2 p-4',
    default: 'gap-4 p-5',
    lg: 'gap-4 p-6',
};

function CardContent({ className, size = 'default', ...props }: React.ComponentProps<'div'> & { size?: CardSize }) {
    return (
        <div
            data-slot="card-content"
            className={cn('bg-surface-tertiary flex flex-col', cardContentSizeVariants[size], className)}
            {...props}
        />
    );
}

// ─── CardFooter ───────────────────────────────────────────────────────────────

/**
 * The footer of a Card, backed by ActionFooter. Supports the same
 * primaryAction / secondaryAction / tertiaryAction props, or pass children
 * for fully custom content.
 *
 * @example
 * ```tsx
 * <CardFooter
 *   size="default"
 *   tertiaryAction={{ label: 'Delete', onClick: handleDelete }}
 *   secondaryAction={{ label: 'Cancel', onClick: handleCancel }}
 *   primaryAction={{ label: 'Save', onClick: handleSave }}
 * />
 * ```
 */
function CardFooter({ size = 'default', ...props }: Omit<ActionFooterProps, 'noBorder'>) {
    return <ActionFooter data-slot="card-footer" size={size} {...props} />;
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };

// Re-export ToolbarTitle and ActionFooter for single-import convenience
export { ToolbarTitle, ActionFooter };
export type { ActionFooterProps };
